import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useInputTags, useSuggestions, useInputMode } from './hooks';
import { TagsContainer, SuggestionsDropdown } from './components';
import Spinner from 'react/components/common/Spinner/Spinner';
import './InputBar.scss';

const InputBar = ({
  value = [],
  onChange,
  onValidate,
  onComplete,
  onDefineInterest,
  getSuggestions,
  limit = 100,
  placeholder = 'Add interest',
  placeholderInvalid,
  customSuggestions = [],
  disableAutocomplete = false,
  disableBooleanLogic = false,
  programBlAllowed = false,
  limitationExceededMsg,
  useTags = true,
  className = '',
}) => {
  const [commitInProcess, setCommitInProcess] = useState(false);

  const containerRef = useRef(null);
  const resultsScrollableRef = useRef(null);

  const splitDelimiter = ',';
  const joinDelimiter = ', ';

  // Custom hooks
  const {
    tags,
    setTags,
    removeTag,
    clearAll: clearAllTags,
    executeLimitConstraint,
    convertInputToTags,
  } = useInputTags({ value, limit, limitationExceededMsg, onChange });

  const {
    suggestions,
    setSuggestions,
    isSuggestionsOpen,
    setIsSuggestionsOpen,
    selectedIndex,
    setSelectedIndex,
    updateSuggestions,
    navigateSuggestions,
    resetSuggestions,
  } = useSuggestions({ customSuggestions, disableAutocomplete, getSuggestions });

  const {
    inputValue,
    setInputValue,
    isInputMode,
    setIsInputMode,
    currentTag,
    textareaRef,
    isMountedRef,
    renderTagsToInput,
    toInputMode: switchToInputMode,
    toTagMode,
    handleInputChange: onInputChange,
  } = useInputMode({ tags, joinDelimiter, limit });

  // Commit changes
  const commit = useCallback(
    async (suggest) => {
      if (commitInProcess) return;

      setCommitInProcess(true);

      let newTags = convertInputToTags({
        inputValue,
        suggestToAdd: suggest,
        currentTag,
        customSuggestions,
        splitDelimiter,
        finalCommit: true,
      });
      newTags = executeLimitConstraint(newTags);

      setTags(newTags);
      setInputValue('');
      setIsInputMode(false);
      setIsSuggestionsOpen(false);
      setSuggestions([]);

      // Validate new tags
      const newTagsToValidate = newTags.filter((tag) => !tag.id);
      if (onValidate && newTagsToValidate.length > 0) {
        try {
          const validatedTags = await onValidate(newTagsToValidate.map((t) => t.text));

          // Update tags with validated results
          const finalTags = newTags.map((tag) => {
            if (!tag.id) {
              const validated = validatedTags.find((v) => v.text === tag.text || v.origin === tag.text);
              return validated || tag;
            }
            return tag;
          });

          setTags(finalTags);
          if (onChange) onChange(finalTags);
          if (onComplete) onComplete(finalTags);
        } catch (error) {
          console.error('Validation error:', error);
        }
      } else {
        if (onChange) onChange(newTags);
        if (onComplete) onComplete(newTags);
      }

      if (isMountedRef.current) {
        setCommitInProcess(false);
      }
    },
    [
      commitInProcess,
      convertInputToTags,
      inputValue,
      currentTag,
      customSuggestions,
      executeLimitConstraint,
      setTags,
      setInputValue,
      setIsInputMode,
      setIsSuggestionsOpen,
      setSuggestions,
      onValidate,
      onChange,
      onComplete,
      isMountedRef,
    ]
  );

  // Handle input change
  const handleInputChange = (e) => {
    onInputChange(e, splitDelimiter, (newCurrentTag) => {
      setSelectedIndex(0);
      updateSuggestions(newCurrentTag, isInputMode);
    });
  };

  // Handle key down
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const selectedSuggestion = suggestions[selectedIndex];
      commit(selectedSuggestion?.text);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (suggestions.length > 0) {
        e.preventDefault();
        navigateSuggestions(e.key === 'ArrowUp' ? 'up' : 'down', resultsScrollableRef);
      }
    }
  };

  // Switch to input mode
  const toInputMode = () => {
    switchToInputMode(resetSuggestions);
  };

  // Clear all
  const clearAll = () => {
    clearAllTags();
    setSuggestions([]);
  };

  // Click suggestion
  const clickSuggestion = (suggestion) => {
    commit(suggestion.text);
  };

  const containerClasses = [
    'input-bar',
    isInputMode ? 'input-mode' : 'tags-mode',
    isSuggestionsOpen ? 'open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className={containerClasses}>
      <i className="icon-Search"></i>

      {commitInProcess && (
        <div className="input-bar-spinner">
          <Spinner show isSmall />
        </div>
      )}

      <span className="input-bar-right-buttons">
        {tags.length > 0 && (
          <span className="clear-all" onClick={clearAll}>
            Clear all
          </span>
        )}
      </span>

      <label onClick={toInputMode}>
        {tags.length > 0 && (
          <TagsContainer
            tags={tags}
            useTags={useTags}
            placeholderInvalid={placeholderInvalid}
            onRemoveTag={removeTag}
          />
        )}

        <div className={`tags-input ${tags.length ? 'textarea-hidden' : ''}`}>
          <textarea
            ref={textareaRef}
            className={`input ${tags.length ? 'out-of-view' : ''}`}
            placeholder={placeholder}
            value={isInputMode ? inputValue : renderTagsToInput()}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={toTagMode}
            onFocus={() => {
              if (!isInputMode) {
                toInputMode();
              }
            }}
          />
        </div>
      </label>

      {isSuggestionsOpen && suggestions.length > 0 && (
        <SuggestionsDropdown
          suggestions={suggestions}
          selectedIndex={selectedIndex}
          currentTag={currentTag}
          programBlAllowed={programBlAllowed}
          disableBooleanLogic={disableBooleanLogic}
          resultsScrollableRef={resultsScrollableRef}
          onClickSuggestion={clickSuggestion}
          onDefineInterest={onDefineInterest}
          tags={tags}
        />
      )}
    </div>
  );
};

InputBar.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  onValidate: PropTypes.func,
  onComplete: PropTypes.func,
  onDefineInterest: PropTypes.func,
  getSuggestions: PropTypes.func,
  limit: PropTypes.number,
  placeholder: PropTypes.string,
  placeholderInvalid: PropTypes.string,
  customSuggestions: PropTypes.array,
  disableAutocomplete: PropTypes.bool,
  disableBooleanLogic: PropTypes.bool,
  programBlAllowed: PropTypes.bool,
  limitationExceededMsg: PropTypes.string,
  useTags: PropTypes.bool,
  className: PropTypes.string,
};

export default InputBar;
